import { useCallback, useEffect, useMemo, useState } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import {
  DestinationType_Enum,
  MessageType_Enum,
  SenderType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { RelatedID } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { isEqual } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { DepartmentsQueries } from '@frontend/api-departments';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { CommPreferenceQueries, MessagesHooks, MessagesTypes, MessagesUtils } from '@frontend/api-messaging';
import { PersonsV3 } from '@frontend/api-person';
import { PetsV1 } from '@frontend/api-pets';
import { ReviewInvitationMutations } from '@frontend/api-reviews-invitation';
import { SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { useDraft } from '@frontend/api-sms-draft';
import { SMSSendV3 } from '@frontend/api-sms-send';
import { SMSSignatureV1 } from '@frontend/api-sms-signature';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { InboxUtilsHooks } from '@frontend/inbox-api-utils';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import type { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { ThreadSendingAreaUtils } from '@frontend/thread-sending-area';
import { useDebouncedValue, useFileUpload, useAlert, ModalProps } from '@frontend/design-system';
import type { SendInThreadStateProps } from '../components/send-in-thread/send-in-thread-view';
import { TEMPLATE_TYPE_LINK_MAP } from '../constants';
import type { SendInThreadModalProps } from '../modals/send-in-thread-modal';
import { LinkData, PropertyBindingsData } from '../types';
import { convertBindingsDataToPropertyBindingsList } from '../utils/convert-bindings-data-to-property-bindings-list';
import { convertLinkDataToProperty } from '../utils/convert-link-data-to-property';
import { getPetBindingFromAppointmentData } from '../utils/get-pet-binding-from-appointment-data';
import { reducePetRelatedAppointmentData } from '../utils/reduce-pet-related-appointment-data';
import { useGetDefaultTemplate } from './use-get-default-template';
import { useThreadMedia } from './use-thread-media';

type UseSendInThreadStateArgs = Omit<SendInThreadModalProps, 'onBack' | 'onSend' | keyof ModalProps> & {
  propertyBindings?: PropertyBindingsData;
  sendToCustomPhoneNumber?: boolean;
  messageType?: MessageType_Enum;
  onSend?: NonNullable<NonNullable<Parameters<typeof SMSSendV3.Mutations.useSendMutation>[0]>['options']>['onSuccess'];
  onSendError?: NonNullable<
    NonNullable<Parameters<typeof SMSSendV3.Mutations.useSendMutation>[0]>['options']
  >['onError'];
  onSchedule?: NonNullable<
    NonNullable<Parameters<typeof ManualSmsScheduledV1.Mutations.useScheduleMutation>[0]>['options']
  >['onSuccess'];
  onScheduleError?: NonNullable<
    NonNullable<Parameters<typeof ManualSmsScheduledV1.Mutations.useScheduleMutation>[0]>['options']
  >['onError'];
};

type SendInThreadState = {
  contentProps: SendInThreadStateProps;
  sendMessage: () => void;
  scheduleMessage: (scheduledTime: Date | string, pausable: boolean) => void;
  setDraftToRenderedTemplate: (template: Template, propertyBindings?: PropertyBindingsData) => void;
};

export const useSendInThreadState = ({
  groupId,
  initThreadId,
  initPersonPhone,
  personId,
  petId,
  onSend,
  onSendError,
  onSchedule,
  onScheduleError,
  selectedTemplate,
  messageType,
  onThreadChange,
  templateType,
  sendToCustomPhoneNumber,
  linkData,
  propertyBindings,
  initialBody,
  initialMediaIds,
}: UseSendInThreadStateArgs): SendInThreadState => {
  const { t } = useTranslation('integrated-messaging');
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { selectedOrgId, getLocationName, accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const { featureFlags } = useAppFlagStore();
  const locationData = accessibleLocationData[groupId];
  const currentUser = getUser();
  const [bodyValue, setBodyValue] = useState(initialBody ?? '');
  const [dynamicModalContextSubtitle, setDynamicModalContextSubtitle] = useState<string>();
  const [previousThreadId, setPreviousThreadId] = useState(initThreadId);
  const debouncedBodyValue = useDebouncedValue(bodyValue, 1000);
  const [selectedPersonPhone, setSelectedPersonPhone] = useState(initPersonPhone);
  const [selectedLocationPhone, setSelectedLocationPhone] = useState<string>();
  const { getDataFromLocationPhone } = InboxUtilsHooks.useDepartmentLocationPhoneMappings();
  const selectedDepartmentId = useMemo(() => {
    return getDataFromLocationPhone(selectedLocationPhone ?? '')?.departmentId;
  }, [getDataFromLocationPhone, selectedLocationPhone]);
  const [threadSelectionHasChanged, setThreadSelectionHasChanged] = useState(false);
  const getDefaultTemplate = useGetDefaultTemplate({
    groupIds: [groupId],
  });
  const [initTemplate, setInitTemplate] = useState<Template>();
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const isVet = lastUsedVertical === Vertical.VET;
  const aclEnforcementEnabled = !!featureFlags[groupId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')
    ?.value;
  const disableAccess = aclEnforcementEnabled ? !hasSchemaACL(groupId, Permission.SMS_INBOX_MANAGE) : false;
  const personQuery = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      locationIds: [groupId],
      personId: personId ?? '',
    },
    {
      enabled: !!personId,
      select: (data) =>
        data
          ? {
              ...data,
              contactInfo: data.contactInfo?.filter(
                (contact) => contact.type !== ContactType_Enum.EMAIL && !!contact.destination
              ),
            }
          : undefined,
    }
  );

  const petNameEnabled = !!featureFlags[groupId]?.get('nwx:manual-templates-pet-name')?.value;
  const petsQuery = PetsV1.Queries.useFetchPetsByPersonIdQuery(
    {
      personId: personId ?? '',
      locationId: groupId,
    },
    {
      enabled: !!personId && isVet && petNameEnabled,
    }
  );
  const { data: appointmentsData } = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId: personId ?? '',
      includePast: true,
    },
    {
      enabled: !!personId && isVet && petNameEnabled,
      select: (data) => reducePetRelatedAppointmentData(data.appointments, petsQuery.data?.pets ?? [], petId),
    }
  );
  const departmentsQuery = DepartmentsQueries.useListDefaultSMSQuery({ locationId: groupId });
  const { data: locationPhonesData } = InboxLocationPhones.Hooks.useLocationPhones({ locationIds: [groupId] });
  const locationPhonesByLocation = useMemo<Record<string, string[]>>(() => {
    return {
      [groupId]: locationPhonesData.locationPhonesByLocation[groupId]?.map((phone) => phone.locationPhone) ?? [],
    };
  }, [groupId, locationPhonesData.locationPhonesByLocation]);

  const personPhones = personQuery.data?.contactInfo?.map((contact) => contact.destination ?? '').filter(Boolean) ?? [];
  const v4ThreadsStatusQuery = SMSDataV4.Queries.useBatchGetThreadStatusQuery(
    {
      requests: SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
        personPhones,
        locationPhonesByLocation: locationPhonesByLocation,
      }),
    },
    {
      enabled: personPhones.length > 0,
      select: (data) => {
        const sortedAllThreads = data.threadStatuses.sort((a, b) => {
          const aLastModified = a.lastModified ?? '';
          const bLastModified = b.lastModified ?? '';
          return bLastModified.localeCompare(aLastModified);
        });
        const lastUpdatedThread = sortedAllThreads[0];
        return {
          lastUpdatedThread,
          sortedAllThreads,
        };
      },
    }
  );
  const initialThread = initThreadId
    ? v4ThreadsStatusQuery.data?.sortedAllThreads.find((thread) => thread.threadId === initThreadId)
    : initPersonPhone
    ? v4ThreadsStatusQuery.data?.sortedAllThreads.find(
        (thread) => formatPhoneNumberE164(thread.personPhone) === formatPhoneNumberE164(initPersonPhone)
      )
    : v4ThreadsStatusQuery.data?.lastUpdatedThread;

  const needsToLookupThreadId =
    (!!selectedPersonPhone && (selectedPersonPhone !== initPersonPhone || !!selectedLocationPhone)) || !initialThread;
  const threadIdRequest = useMemo(
    () => ({
      locationId: groupId,
      personPhone: selectedPersonPhone ? formatPhoneNumberE164(selectedPersonPhone) : '',
      locationPhone: selectedLocationPhone ?? '',
    }),
    [groupId, selectedPersonPhone, selectedLocationPhone]
  );
  const v4ThreadIdQuery = SMSDataV4.Queries.useGetThreadIdQuery(threadIdRequest, {
    enabled: needsToLookupThreadId,
  });
  const isCalculatedThread = v4ThreadIdQuery.data?.calculated;

  const { data: smsPreference } = CommPreferenceQueries.useCheckSMSPreference({
    userChannelAddress: selectedPersonPhone ? formatPhoneNumberE164(selectedPersonPhone) : '',
    locationId: groupId,
    messageType: messageType ?? MessageType_Enum.MESSAGING_MANUAL,
    channel: Channel.CHANNEL_SMS,
  });

  const threadId = threadSelectionHasChanged
    ? v4ThreadIdQuery.isFetching
      ? undefined
      : v4ThreadIdQuery.data?.threadId
    : initThreadId;
  const personData = {
    personId: personQuery.data?.personId ?? '',
    firstName: personQuery.data?.firstName ?? '',
    preferredName: personQuery.data?.preferredName ?? '',
    lastName: personQuery.data?.lastName ?? '',
  };
  const { deleteMutation, shallowUpdate } = useDraft({
    threadId: threadId ?? '',
    userId: currentUser?.userID ?? '',
    orgId: selectedOrgId,
    locationId: groupId,
    groupIds: selectedLocationIds,
    personData,
  });

  const { getThreadLocationPhone } = SMSDataSharedUtils.threadItemsUtils;

  const {
    metadata,
    threadItems,
    scheduledMessages,
    isLoadingFirstPage,
    hasOlderMessages,
    fetchOlderMessages,
    mediaQueries,
  } = MessagesHooks.useThread({
    threadId: threadId ?? '',
    groupId,
    providedPersonPhone: selectedPersonPhone,
    getThreadRetryLimit: 1,
    disable: disableAccess,
    isNew: threadSelectionHasChanged && isCalculatedThread,
  });
  const selectedDepartmentQuery = DepartmentsQueries.useListDefaultSMSQuery(
    { locationId: groupId },
    {
      select: (data) => {
        return data.smsNumbers?.find((number) => number.id === selectedDepartmentId);
      },
      enabled: !!selectedDepartmentId,
    }
  );

  const locationPhone =
    departmentsQuery.data?.smsNumbers?.find((dept) => dept.id === selectedDepartmentId)?.smsNumber?.number ||
    getThreadLocationPhone(threadItems[0]) ||
    '';

  const onSelectPersonPhone = (selection: PersonPhoneSelection) => {
    if (!threadSelectionHasChanged) setThreadSelectionHasChanged(true);
    setSelectedPersonPhone(selection.personPhone);
  };

  const onSelectLocationPhone = (selection: LocationPhoneSelection) => {
    if (!threadSelectionHasChanged) setThreadSelectionHasChanged(true);
    setSelectedLocationPhone(selection.locationPhone);
  };

  const { threadMedia, uploadFiles, removeMediaItem, clearMedia } = useThreadMedia({
    threadId: threadId ?? '',
    groupId,
    departmentId: selectedDepartmentId ?? '',
    personPhone: selectedPersonPhone ?? '',
    locationPhone,
    maxFileCount: 10,
    loadDraft: false,
    personData,
    onError: () => {
      alert.error(t('Error uploading image. Please try again.'));
    },
    onExceedMaxFileCount: (excessCount) => {
      alert.error(
        t('You can only send up to 10 images in a single message. {{count}} images were not uploaded.', {
          count: excessCount,
        })
      );
    },
    mediaIds: initialMediaIds,
  });

  const imageUploadProps = useFileUpload({
    acceptedFileType: ['jpeg', 'png', 'jpg'],
    onFileUpload: (files) => {
      uploadFiles(files);
      imageUploadProps.resetFiles();
    },
  });

  const clearDraft = () => {
    deleteMutation.mutate({
      threadId: threadId ?? '',
      userId: currentUser?.userID ?? '',
      orgId: selectedOrgId,
      locationId: groupId,
    });
    clearMedia();
    setBodyValue('');
  };

  const { data: signature } = SMSSignatureV1.Queries.useGetSignatureQuery({
    request: {
      groupId,
      userId: currentUser?.userID ?? '',
    },
    options: {
      enabled: !!currentUser?.userID,
      select: (data) => data.signature.signature,
    },
  });
  const { mutate: sendMessage } = SMSSendV3.Mutations.useSendMutation({
    options: {
      onSuccess: (...args) => {
        clearDraft();
        // When thread was calculated (new), invalidate GetThreadId so `calculated`
        // updates to false on next fetch. This transitions `isNew` → false, re-enabling thread queries.
        if (isCalculatedThread) {
          queryClient.invalidateQueries({ queryKey: SMSDataV4.Queries.getGetThreadIdQueryKey(threadIdRequest) });
        }
        onSend?.(...args);
      },
      onError: (...args) => {
        onSendError?.(...args);
      },
    },
    optimisticUpdate: true,
  });

  const { mutate: scheduleMessage } = ManualSmsScheduledV1.Mutations.useScheduleMutation({
    options: {
      onSuccess: (...args) => {
        clearDraft();
        if (isCalculatedThread) {
          queryClient.invalidateQueries({ queryKey: SMSDataV4.Queries.getGetThreadIdQueryKey(threadIdRequest) });
        }
        onSchedule?.(...args);
      },
      onError: (...args) => {
        onScheduleError?.(...args);
      },
    },
    optimisticUpdate: true,
  });

  const { associatedContacts, isLoading: isGetAssociatedContactsLoading } = PersonsV3.PersonHooks.useAssociatedContacts(
    {
      phoneNumber: selectedPersonPhone ?? '',
      locationId: groupId,
      enabled: !!sendToCustomPhoneNumber && !!selectedPersonPhone,
    }
  );

  const [renderData, setRenderData] = useState<{
    payload: Parameters<(typeof TemplatorV2Queries)['useRender']>[0];
    templateId: string;
    linkData?: LinkData;
  }>();
  const renderedTemplateQuery = TemplatorV2Queries.useRender(
    renderData?.payload
      ? {
          ...renderData.payload,
          keepEmptyDynamicFields: true,
        }
      : {
          templateTypeSlug: TemplateType_Slug.UNSPECIFIED_TEMPLATE_TYPE,
          templateString: '',
          bindingsList: [],
          timezone: locationData?.timezone ?? '',
          // TODO: get the locale dynamically once we have a way to do so
          locale: 'en_US',
          destinationType: DestinationType_Enum.SMS,
        },
    {
      enabled: !!renderData,
      onSuccess: ({ message: { message } }) => {
        setBodyValue(message);
      },
    }
  );

  const mediaIds = threadMedia.reduce<string[]>((acc, media) => {
    if (media.mediaObj?.mediaId) acc.push(media.mediaObj.mediaId);
    return acc;
  }, []);

  const getRelatedIds = (body: string): RelatedID[] => {
    // To handle setting relatedIds for review request using reviewInvitation data after selecting template,
    const { invitation: reviewInvitation } = reviewInvitationData || {};
    if (
      templateType &&
      [TemplateType_Slug.MANUAL_REVIEW_REQUEST, TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET].includes(templateType) &&
      reviewInvitation?.invitationId
    ) {
      return [
        {
          type: RelatedType.RELATED_TYPE_REVIEW,
          id: reviewInvitation?.invitationId,
        },
      ];
    }

    if (!templateType || !linkData || !body.includes(linkData.link)) return [];

    const linkMap = TEMPLATE_TYPE_LINK_MAP[templateType];
    return [
      {
        type: linkMap.relatedType,
        id: linkData.relatedId,
      },
    ];
  };

  const getLabels = (relatedIds: RelatedID[]) => {
    return relatedIds.reduce<Parameters<typeof sendMessage>[0]['labels']>((acc, curr) => {
      if (curr.type === RelatedType.RELATED_TYPE_FORM) {
        if (acc) {
          acc[curr.type] = curr.id;
        } else {
          acc = { [curr.type]: curr.id };
        }
      }
      return acc;
    }, undefined);
  };

  const handleSend = () => {
    if (disableAccess) {
      alert.error(t('You do not have permission to send messages.'));
      return;
    }
    if (bodyValue.length > ThreadSendingAreaUtils.MAX_CHARACTER_LIMIT) return;
    const relatedIds = getRelatedIds(bodyValue);
    sendMessage({
      locationId: groupId,
      personPhone: selectedPersonPhone ? MessagesUtils.formatPhoneWithCountryCode(selectedPersonPhone) : '',
      departmentId: selectedDepartmentId,
      body: `${bodyValue}\n\n${signature}`.trim(),
      media: mediaIds.map((mediaId) => ({ mediaId })),
      programSlugId: MessagesTypes.KnownProgramSlugIds.MANUAL_MESSAGES,
      relatedIds,
      personId: sendToCustomPhoneNumber ? metadata?.person?.personId ?? '' : personId, // To support sending empty personId for unknown selectedPhoneNumbers
      createdBy: currentUser?.userID,
      messageType: ThreadSendingAreaUtils.getMessageTypeFromRelatedIds(relatedIds),
      labels: getLabels(relatedIds), // This is needed for the forms team to get the submission id on sms update events
      senderType: SenderType_Enum.HUMAN,
      _otherOptions: threadId
        ? {
            threadId,
          }
        : undefined,
    });
  };

  const handleSchedule = (scheduledTime: Date | string, pausable: boolean) => {
    if (disableAccess) {
      alert.error(t('You do not have permission to send messages.'));
      return;
    }
    if (bodyValue.length > ThreadSendingAreaUtils.MAX_CHARACTER_LIMIT) return;
    const relatedIds = getRelatedIds(bodyValue);
    scheduleMessage({
      locationId: groupId,
      threadId: threadId ?? '',
      departmentId: selectedDepartmentId ?? '',
      locationPhone: formatPhoneNumberE164(locationPhone),
      personPhone: selectedPersonPhone ? formatPhoneNumberE164(selectedPersonPhone) : '',
      personId: sendToCustomPhoneNumber ? metadata?.person?.personId ?? '' : personId, // To support sending empty personId for unknown selectedPhoneNumbers
      body: `${bodyValue}\n\n${signature}`.trim(),
      mediaIds,
      scheduledBy: currentUser?.userID ?? '',
      sendAt: typeof scheduledTime === 'string' ? scheduledTime : scheduledTime.toISOString(),
      relatedIds,
      pausable,
      messageType: ThreadSendingAreaUtils.getMessageTypeFromRelatedIds(relatedIds),
    });
  };

  useEffect(() => {
    if (renderedTemplateQuery.data) {
      setBodyValue(renderedTemplateQuery.data.message.message);
    }
  }, [renderedTemplateQuery.data]);

  useEffect(() => {
    if (previousThreadId)
      shallowUpdate({
        threadId: previousThreadId,
        draft: { body: debouncedBodyValue },
        departmentId: selectedDepartmentId ?? '',
        personPhone: selectedPersonPhone ?? '',
        locationPhone,
      });
  }, [debouncedBodyValue]);

  useEffect(() => {
    if (metadata?.locationPhone && !selectedLocationPhone) setSelectedLocationPhone(metadata.locationPhone);
  }, [metadata?.locationPhone]);

  useEffect(() => {
    if (threadId && previousThreadId !== threadId) {
      if (previousThreadId)
        deleteMutation.mutate({ threadId: previousThreadId, userId: currentUser?.userID ?? '', orgId: selectedOrgId });
      shallowUpdate({
        threadId,
        departmentId: selectedDepartmentId ?? '',
        personPhone: selectedPersonPhone ?? '',
        locationPhone,
        draft: {
          body: bodyValue,
          medias: threadMedia.map((media) => ({
            mediaId: media.mediaObj?.mediaId || media.id,
          })),
        },
      });
      setPreviousThreadId(threadId);
    }
  }, [threadId]);

  useEffect(() => {
    if (threadId)
      shallowUpdate({
        threadId,
        departmentId: selectedDepartmentId ?? '',
        personPhone: selectedPersonPhone ?? '',
        locationPhone,
        draft: {
          medias:
            initialMediaIds?.map((mediaId) => ({
              mediaId,
            })) ?? [],
        },
      });
  }, [JSON.stringify(initialMediaIds)]);

  useEffect(() => {
    if ((initialThread?.threadId || !!personQuery.data?.personId) && !initThreadId) {
      setSelectedPersonPhone(initialThread?.personPhone ?? personQuery.data?.contactInfo?.[0]?.destination);
      if (initialThread?.locationPhone) setSelectedLocationPhone(initialThread?.locationPhone);
      if (initialThread?.threadId) setPreviousThreadId(initialThread?.threadId);
      setThreadSelectionHasChanged(true);
    }
  }, [initialThread?.threadId, personQuery.data?.personId]);

  const petName = useMemo(
    () =>
      getPetBindingFromAppointmentData(
        appointmentsData ?? { allAppointments: [], futureAppointmentCount: 0, latestAppointment: undefined },
        petsQuery.data?.pets ?? [],
        linkData?.relatedType === RelatedType.RELATED_TYPE_APPOINTMENT ? linkData.relatedId : undefined
      ),
    [JSON.stringify(appointmentsData), JSON.stringify(petsQuery.data?.pets), linkData?.relatedType, linkData?.relatedId]
  );

  const defaultBindingsData = useMemo<PropertyBindingsData>(
    () => ({
      [DynamicFieldProperty_Enum.ORG_NAME]: getLocationName(selectedOrgId),
      [DynamicFieldProperty_Enum.BUSINESS_GROUP_NAME]: getLocationName(groupId),
      [DynamicFieldProperty_Enum.BUSINESS_GROUP_PHONE]:
        selectedDepartmentQuery.data?.smsNumber?.number || getThreadLocationPhone(threadItems?.[0]),
      [DynamicFieldProperty_Enum.FIRST_NAME]: personQuery.data?.firstName,
      [DynamicFieldProperty_Enum.LAST_NAME]: personQuery.data?.lastName,
      [DynamicFieldProperty_Enum.PREFERRED_NAME]: personQuery.data?.preferredName || personQuery.data?.firstName,
      [DynamicFieldProperty_Enum.PET_NAME]: petName || '[Pet Name]',
    }),
    [
      groupId,
      selectedOrgId,
      personQuery.data?.firstName,
      personQuery.data?.lastName,
      personQuery.data?.preferredName,
      threadItems?.[0],
      getLocationName,
      petName,
    ]
  );

  const { mutateAsync: reviewInvitationMutate, data: reviewInvitationData } =
    ReviewInvitationMutations.useReviewInvitationMutation();

  const setDraftToRenderedTemplate = useCallback<SendInThreadState['setDraftToRenderedTemplate']>(
    async (template, propertyBindingsData = {}) => {
      const linkDataPropertyBindings =
        linkData && templateType ? convertLinkDataToProperty({ linkData, templateType }) : undefined;

      //Get new review link before rendering a review template (except static ones).
      let newReviewLink: string | undefined;

      if (
        templateType &&
        [TemplateType_Slug.MANUAL_REVIEW_REQUEST, TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET].includes(templateType)
      ) {
        const {
          invitation: { invitationLink },
        } = await reviewInvitationMutate({
          locationId: groupId,
          personId,
          ...(!template.templateId.includes('static') && { templateId: template.templateId }),
        });
        newReviewLink = invitationLink || '';
        setDynamicModalContextSubtitle(invitationLink);
      }
      const bindingsList = convertBindingsDataToPropertyBindingsList({
        ...defaultBindingsData,
        ...linkDataPropertyBindings,
        ...propertyBindings,
        ...propertyBindingsData,
        ...(newReviewLink && { REVIEW_REQUEST_LINK: newReviewLink }),
      });
      setRenderData((prev) => {
        const newPayload = {
          templateTypeSlug: template.templateTypeSlug,
          templateString: template.templateString,
          bindingsList,
          timezone: locationData?.timezone ?? '',
          // TODO: get the locale dynamically once we have a way to do so
          locale: 'en_US',
          destinationType: DestinationType_Enum.SMS,
        };

        if (renderedTemplateQuery.data && isEqual(newPayload, prev?.payload)) {
          setBodyValue(renderedTemplateQuery.data.message.message);
        }

        return {
          payload: newPayload,
          templateId: template.templateId,
        };
      });
    },
    [
      selectedOrgId,
      defaultBindingsData,
      propertyBindings,
      locationData?.timezone,
      setRenderData,
      linkData,
      templateType,
      convertLinkDataToProperty,
      renderedTemplateQuery?.data,
      setBodyValue,
    ]
  );

  useEffect(() => {
    if (templateType) {
      if (selectedTemplate) {
        setInitTemplate(selectedTemplate);
        return;
      }
      const template = getDefaultTemplate({ type: templateType });
      if (template) setInitTemplate(template);
    }
  }, [groupId, templateType]);

  useEffect(() => {
    const hasPerson = personId ? !!personQuery.data : true;
    if (hasPerson && initTemplate) {
      setDraftToRenderedTemplate(initTemplate);
    }
  }, [initTemplate, personId, personQuery.data, JSON.stringify(linkData), JSON.stringify(propertyBindings)]);

  useEffect(() => {
    if (threadId) {
      onThreadChange?.({
        threadId,
        personPhone: selectedPersonPhone || '',
        locationPhone:
          selectedDepartmentQuery.data?.smsNumber?.number || getThreadLocationPhone(threadItems?.[0]) || '',
        departmentId: selectedDepartmentId || '',
      });
    }
  }, [threadId]);

  // Thread context changes for custom phone number. metadata represents thread context
  const isThreadContextChangedWithCustomNumber = useMemo(
    () =>
      !!sendToCustomPhoneNumber &&
      !isGetAssociatedContactsLoading &&
      !associatedContacts.find((person) => person.personId === personId),
    [sendToCustomPhoneNumber, associatedContacts]
  );

  return {
    contentProps: {
      imageUploadProps,
      groupId,
      threadId: threadId ?? '',
      person: personQuery.data,
      selectedPersonPhone: selectedPersonPhone ?? '',
      onSelectPersonPhone,
      optedOut: !!selectedPersonPhone && smsPreference?.consented === false,
      selectedLocationPhone: { locationPhone: selectedLocationPhone, locationId: groupId },
      onSelectLocationPhone,
      bodyValue,
      dynamicModalContextSubtitle,
      setBodyValue,
      onSend: handleSend,
      onSchedule: handleSchedule,
      threadItems,
      scheduledMessages,
      isLoadingFirstPage,
      hasOlderMessages,
      mediaQueries,
      fetchOlderMessages,
      threadMedia,
      removeMediaItem,
      personInThreadContext: isThreadContextChangedWithCustomNumber ? metadata?.person : undefined, // for when thread body loads messages from different contact (custom number) while the header and sending context doesn't change.
      isUnknownContactThread:
        isThreadContextChangedWithCustomNumber && threadItems.length === 0 && !!selectedPersonPhone,
    },
    sendMessage: handleSend,
    scheduleMessage: handleSchedule,
    setDraftToRenderedTemplate,
  };
};
